(() => {
  'use strict';

  if (window.__TSM_CART_DEPENDENCY_FIX_V10__) return;
  window.__TSM_CART_DEPENDENCY_FIX_V10__ = true;

  const config = window.TSM_CART_DEPENDENCIES || {};
  const BASE_ID = Number(config.baseProductId || 0);
  const ADDON_ID = Number(config.addonProductId || 0);
  const GIFT_ID = Number(config.giftProductId || 0);
  let GIFT_VARIANT_ID = Number(config.giftVariantId || 0);
  const FAST_VARIANT_IDS = new Set(
    (Array.isArray(config.fastVariantIds) ? config.fastVariantIds : [])
      .map((id) => Number(id || 0))
      .filter((id) => id > 0)
  );

  const BASE_HANDLE = config.baseHandle || 'the-shift-method-magyar';
  const ADDON_HANDLE = config.addonHandle || '24-oras-gyorsitott';
  const GIFT_HANDLE = config.giftHandle || 'vezetett-manifesztacios-naplo';

  const rootUrl =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = `${rootUrl}cart`;
  const checkoutUrl = '/checkout';
  const drawerSections = ['cart-drawer', 'cart-icon-bubble'];

  // Checkout uses short limits so gift synchronization cannot create a long delay.
  // Normal cart updates keep a longer timeout for slower connections.
  const DEFAULT_REQUEST_TIMEOUT = 7000;
  const CHECKOUT_CART_TIMEOUT = Number(config.checkoutCartTimeout || 1200);
  const CHECKOUT_MUTATION_TIMEOUT = Number(config.checkoutMutationTimeout || 1800);
  const CHECKOUT_TOTAL_TIMEOUT = Number(config.checkoutTotalTimeout || 3200);

  let busy = false;
  let checkoutStarted = false;
  let activeController = null;
  let observerTimer = 0;
  let previousSnapshot = null;

  const normalizedPath = (value) => String(value || '').replace(/\/+$/, '');
  const isCartPage = () =>
    normalizedPath(window.location.pathname) ===
    normalizedPath(new URL(cartUrl, window.location.origin).pathname);

  const itemHandle = (item) => {
    if (item && typeof item.handle === 'string' && item.handle) return item.handle;
    if (item && typeof item.product_handle === 'string' && item.product_handle) {
      return item.product_handle;
    }

    const url = item && typeof item.url === 'string' ? item.url : '';
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const matchesItem = (item, id, handle) => {
    const productId = Number(item && item.product_id ? item.product_id : 0);
    if (id > 0 && productId === id) return true;
    return itemHandle(item) === handle;
  };

  const isBaseItem = (item) => matchesItem(item, BASE_ID, BASE_HANDLE);
  const isAddonItem = (item) => matchesItem(item, ADDON_ID, ADDON_HANDLE);
  const isGiftItem = (item) => matchesItem(item, GIFT_ID, GIFT_HANDLE);

  // The journal can also be purchased separately. Only a journal line carrying
  // this private bundle property is managed as the free 24-hour gift.
  const itemProperty = (item, name) => {
    const properties = item && item.properties;
    if (!properties || typeof properties !== 'object') return '';
    return String(properties[name] || '');
  };

  const isBundleGiftItem = (item) =>
    isGiftItem(item) && itemProperty(item, '_tsm_bundle_gift') === ADDON_HANDLE;

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const itemSearchText = (item) => {
    const optionText = Array.isArray(item && item.options_with_values)
      ? item.options_with_values
          .map((option) => `${option && option.name ? option.name : ''} ${option && option.value ? option.value : ''}`)
          .join(' ')
      : '';

    return normalize([
      item && item.title,
      item && item.product_title,
      item && item.variant_title,
      item && item.sku,
      itemHandle(item),
      item && item.url,
      optionText
    ].join(' '));
  };

  const hasFastVersionText = (value) => {
    const text = normalize(value);
    return (
      text.includes('24') &&
      (
        text.includes('ora') ||
        text.includes('oras') ||
        text.includes('hour') ||
        text.includes('gyorsitott') ||
        text.includes('gyorsitas') ||
        text.includes('accelerated') ||
        text.includes('fast')
      )
    );
  };

  const isFastVariantItem = (item) => {
    if (!item) return false;
    if (isAddonItem(item)) return true;
    if (!isBaseItem(item)) return false;

    const variantId = Number(item.variant_id || item.id || 0);
    if (variantId > 0 && FAST_VARIANT_IDS.has(variantId)) return true;

    return hasFastVersionText(itemSearchText(item));
  };

  const abortActiveRequest = () => {
    if (activeController) {
      activeController.abort();
      activeController = null;
    }
  };

  const fetchWithTimeout = async (url, options = {}, timeout = DEFAULT_REQUEST_TIMEOUT) => {
    abortActiveRequest();
    const controller = new AbortController();
    activeController = controller;
    const timer = window.setTimeout(() => controller.abort(), timeout);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      window.clearTimeout(timer);
      if (activeController === controller) activeController = null;
    }
  };

  const fetchCart = async (timeout = DEFAULT_REQUEST_TIMEOUT) => {
    const response = await fetchWithTimeout(`${rootUrl}cart.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }, timeout);

    if (!response.ok) throw new Error('The cart could not be loaded.');
    return response.json();
  };

  const updateCart = async (updates, timeout = DEFAULT_REQUEST_TIMEOUT, includeSections = true) => {
    const body = includeSections
      ? { updates, sections: drawerSections, sections_url: cartUrl }
      : { updates };

    const response = await fetchWithTimeout(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }, timeout);

    if (!response.ok) {
      let message = 'The linked cart items could not be removed.';
      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch (error) {}
      throw new Error(message);
    }

    return response.json();
  };

  const addGiftToCart = async (variantId, timeout = DEFAULT_REQUEST_TIMEOUT, includeSections = true) => {
    const body = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {
            _tsm_bundle_gift: ADDON_HANDLE
          }
        }
      ]
    };

    if (includeSections) {
      body.sections = drawerSections;
      body.sections_url = cartUrl;
    }

    const response = await fetchWithTimeout(`${rootUrl}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }, timeout);

    if (!response.ok) {
      let message = 'The free journal could not be added to the cart.';
      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch (error) {}
      throw new Error(message);
    }

    return response.json();
  };

  const resolveGiftVariantId = async (timeout = DEFAULT_REQUEST_TIMEOUT) => {
    if (GIFT_VARIANT_ID > 0) return GIFT_VARIANT_ID;

    const productUrl = `${rootUrl}products/${encodeURIComponent(GIFT_HANDLE)}.js`;
    const response = await fetchWithTimeout(productUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }, timeout);

    if (!response.ok) throw new Error('The free journal product could not be loaded.');

    const product = await response.json();
    const variant =
      product &&
      Array.isArray(product.variants) &&
      product.variants.find((candidate) => candidate && candidate.available !== false);

    GIFT_VARIANT_ID = Number(variant && variant.id ? variant.id : 0);
    if (!GIFT_VARIANT_ID) throw new Error('No available free journal variant was found.');
    return GIFT_VARIANT_ID;
  };

  const setTriggerLoading = (trigger, loading) => {
    if (!trigger || !trigger.isConnected) return;
    trigger.toggleAttribute('aria-disabled', loading);
    const button = trigger.matches('button') ? trigger : trigger.querySelector('button');
    if (button) button.disabled = loading;
  };

  const parseSection = (html) => {
    if (typeof html !== 'string' || !html.trim()) return null;
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const updateCartIconInPlace = (sections, cartState = null) => {
    const html = sections && sections['cart-icon-bubble'];
    const parsed = parseSection(html);
    const live = document.querySelector('#cart-icon-bubble');
    if (!parsed || !live) return;

    const sourceLink = parsed.querySelector('#cart-icon-bubble');
    const sectionWrapper = parsed.querySelector('#shopify-section-cart-icon-bubble');
    const sourceHtml = sourceLink
      ? sourceLink.innerHTML
      : sectionWrapper
        ? sectionWrapper.innerHTML
        : parsed.body.innerHTML;

    live.innerHTML = sourceHtml;

    if (sourceLink) {
      ['aria-label', 'title', 'data-cart-count'].forEach((name) => {
        if (sourceLink.hasAttribute(name)) live.setAttribute(name, sourceLink.getAttribute(name));
        else live.removeAttribute(name);
      });
    }

    if (cartState && Number(cartState.item_count || 0) === 0) {
      live.querySelectorAll('.cart-count-bubble').forEach((bubble) => bubble.remove());
    }
  };

  const renderDrawerInPlace = (cartState, trigger) => {
    const liveDrawer = document.querySelector('cart-drawer');
    const liveInner = liveDrawer && liveDrawer.querySelector('#CartDrawer .drawer__inner');
    const html = cartState && cartState.sections && cartState.sections['cart-drawer'];
    const parsed = parseSection(html);
    const sourceDrawer = parsed && parsed.querySelector('cart-drawer');
    const sourceInner = parsed && parsed.querySelector('#CartDrawer .drawer__inner');

    if (!liveDrawer || !liveInner || !sourceDrawer || !sourceInner) {
      throw new Error('The refreshed cart drawer content could not be found.');
    }

    const wasOpen = liveDrawer.classList.contains('active');
    liveInner.innerHTML = sourceInner.innerHTML;
    liveDrawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));

    if (typeof liveDrawer.getUpsellHandles === 'function') {
      liveDrawer.upsellHandles = liveDrawer.getUpsellHandles();
    }

    updateCartIconInPlace(cartState.sections || {}, cartState);

    const countdown = liveDrawer.querySelector('countdown-timer');
    if (countdown && typeof countdown.playTimer === 'function') countdown.playTimer();

    window.requestAnimationFrame(() => {
      if (checkoutStarted) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      if (wasOpen) {
        drawer.classList.add('active');
        document.body.classList.add('overflow-hidden');
        return;
      }

      if (typeof drawer.open === 'function') {
        const safeTrigger =
          (trigger && trigger.isConnected && trigger) ||
          document.querySelector('#cart-icon-bubble') ||
          null;
        drawer.open(safeTrigger);
      }
    });
  };

  const rowHandle = (row) => {
    if (!row) return '';
    if (row.dataset && row.dataset.productHandle) return row.dataset.productHandle;

    for (const className of row.classList) {
      if (className.startsWith('cart-item--product-')) {
        return className.slice('cart-item--product-'.length);
      }
    }
    return '';
  };

  const isFastVariantRow = (row) => {
    if (!row) return false;

    const handle = rowHandle(row);
    const productId = Number(row.dataset ? row.dataset.productId || 0 : 0);
    const variantId = Number(row.dataset ? row.dataset.variantId || 0 : 0);
    const isLegacyAddon = handle === ADDON_HANDLE || (ADDON_ID > 0 && productId === ADDON_ID);
    if (isLegacyAddon) return true;

    const isBase = handle === BASE_HANDLE || (BASE_ID > 0 && productId === BASE_ID);
    if (!isBase) return false;
    if (variantId > 0 && FAST_VARIANT_IDS.has(variantId)) return true;

    return hasFastVersionText([
      row.dataset && row.dataset.productTitle,
      row.dataset && row.dataset.variantTitle
    ].join(' '));
  };

  const dependencyModeFromRow = (row) => {
    const handle = rowHandle(row);
    const productId = Number(row && row.dataset ? row.dataset.productId || 0 : 0);

    if (isFastVariantRow(row)) return 'fast';
    if (handle === BASE_HANDLE || (BASE_ID > 0 && productId === BASE_ID)) return 'base';
    return null;
  };

  const buildUpdates = (cart, mode, clickedKey = '', clickedIndex = 0) => {
    const updates = {};

    const isClickedItem = (item, index) =>
      (clickedKey && item.key === clickedKey) ||
      (!clickedKey && clickedIndex > 0 && index + 1 === clickedIndex);

    const remainingItems = cart.items.filter((item, index) => !isClickedItem(item, index));
    const remainingHasBase = remainingItems.some(isBaseItem);
    const remainingHasFast = remainingItems.some(
      (item) => isFastVariantItem(item) && (!isAddonItem(item) || remainingHasBase)
    );

    const currentHasBase = cart.items.some(isBaseItem);
    const currentHasFast = cart.items.some(
      (item) => isFastVariantItem(item) && (!isAddonItem(item) || currentHasBase)
    );

    cart.items.forEach((item, index) => {
      const isClickedLine = isClickedItem(item, index);
      let shouldRemove = isClickedLine;

      if (mode === 'base' || mode === 'fast') {
        if (!remainingHasBase && isAddonItem(item)) shouldRemove = true;
        if (!remainingHasFast && isBundleGiftItem(item)) shouldRemove = true;
      }

      if (mode === 'orphan') {
        if (!currentHasBase && isAddonItem(item)) shouldRemove = true;
        if (!currentHasFast && isBundleGiftItem(item)) shouldRemove = true;
      }

      if (shouldRemove && item.key) updates[item.key] = 0;
    });

    return updates;
  };

  const resetFastBundleState = (phase) => {
    document.dispatchEvent(
      new CustomEvent(`tsm:fast-removal-${phase}`, {
        bubbles: true,
        detail: { source: 'cart-dependency' }
      })
    );
  };

  const enforceDependencyRemoval = async ({
    mode,
    clickedKey = '',
    clickedIndex = 0,
    trigger = null
  } = {}) => {
    if (busy || checkoutStarted || !['base', 'fast', 'orphan'].includes(mode)) return;

    busy = true;
    setTriggerLoading(trigger, true);

    if (mode === 'fast' || mode === 'base') resetFastBundleState('start');

    try {
      const cart = await fetchCart();
      if (checkoutStarted) return;

      const updates = buildUpdates(cart, mode, clickedKey, clickedIndex);
      if (Object.keys(updates).length === 0) return;

      const updatedCart = await updateCart(updates);
      if (checkoutStarted) return;

      if (isCartPage()) {
        window.location.reload();
      } else {
        renderDrawerInPlace(updatedCart, trigger);
      }

      if (mode === 'fast' || mode === 'base' || mode === 'orphan') {
        resetFastBundleState('complete');
      }
    } catch (error) {
      if (error && error.name !== 'AbortError') {
        console.error('[The Shift Method] Cart dependency error:', error);
      }
    } finally {
      setTriggerLoading(trigger, false);
      busy = false;
      previousSnapshot = getDrawerSnapshot();
    }
  };

  const cartRowFromTarget = (target) =>
    target && target.closest ? target.closest('.cart-item') : null;

  const getDrawerSnapshot = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return { base: false, addon: false, fast: false, bundleGift: false };

    const rows = [...drawer.querySelectorAll('.cart-item')];
    const hasHandle = (handle, id) =>
      rows.some((row) => {
        const productId = Number(row.dataset ? row.dataset.productId || 0 : 0);
        return rowHandle(row) === handle || (id > 0 && productId === id);
      });

    return {
      base: hasHandle(BASE_HANDLE, BASE_ID),
      addon: hasHandle(ADDON_HANDLE, ADDON_ID),
      fast: rows.some(isFastVariantRow),
      bundleGift: rows.some(
        (row) => row.dataset && row.dataset.tsmBundleGift === ADDON_HANDLE
      )
    };
  };

  const reconcileDrawerDependencies = () => {
    if (busy || checkoutStarted) return;

    const current = getDrawerSnapshot();
    const previous = previousSnapshot;
    previousSnapshot = current;

    if (!previous) return;

    const fastWasRemoved = previous.fast && !current.fast;
    const baseWasRemoved = previous.base && !current.base;
    const hasOrphan =
      (!current.base && (current.addon || current.bundleGift)) ||
      (!current.fast && current.bundleGift);

    if (fastWasRemoved || baseWasRemoved || hasOrphan) {
      resetFastBundleState('start');
      enforceDependencyRemoval({ mode: 'orphan' });
    }
  };

  const scheduleReconcile = () => {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(reconcileDrawerDependencies, 80);
  };

  const termsAccepted = () => {
    const checkbox = document.querySelector('tnc-chekcbox');
    return !checkbox || checkbox.dataset.checked === 'true';
  };

  const withHardTimeout = (promise, timeout) =>
    new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        abortActiveRequest();
        reject(new Error('The pre-checkout gift check took too long.'));
      }, timeout);

      promise
        .then((value) => {
          window.clearTimeout(timer);
          resolve(value);
        })
        .catch((error) => {
          window.clearTimeout(timer);
          reject(error);
        });
    });

  const ensureGiftStateBeforeCheckout = async () => {
    const cart = await fetchCart(CHECKOUT_CART_TIMEOUT);
    const items = cart.items || [];
    const hasFastVariant = items.some(isFastVariantItem);
    const hasBundleGift = items.some(isBundleGiftItem);

    if (hasFastVariant && !hasBundleGift) {
      const giftVariantId = await resolveGiftVariantId(CHECKOUT_CART_TIMEOUT);
      await addGiftToCart(giftVariantId, CHECKOUT_MUTATION_TIMEOUT, false);
      return;
    }

    if (!hasFastVariant && hasBundleGift) {
      const updates = {};
      items.forEach((item) => {
        if (isBundleGiftItem(item) && item.key) updates[item.key] = 0;
      });

      if (Object.keys(updates).length > 0) {
        await updateCart(updates, CHECKOUT_MUTATION_TIMEOUT, false);
      }
    }
  };

  const setCheckoutButtonLoading = (button, loading) => {
    if (!button) return;

    const label = button.querySelector('.button__label');
    if (label && !button.dataset.tsmOriginalLabel) {
      button.dataset.tsmOriginalLabel = label.textContent;
    }

    button.disabled = loading;
    button.toggleAttribute('aria-busy', loading);

    if (label) {
      label.textContent = loading
        ? 'LOADING…'
        : button.dataset.tsmOriginalLabel || label.textContent;
    }
  };

  const startCheckout = async (event, button) => {
    if (checkoutStarted || !button || button.disabled) return;
    if (!termsAccepted()) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    checkoutStarted = true;
    setCheckoutButtonLoading(button, true);

    try {
      // The drawer can show the gift before it exists as a real line item.
      // Synchronize it before checkout while leaving separately purchased journals untouched.
      await withHardTimeout(ensureGiftStateBeforeCheckout(), CHECKOUT_TOTAL_TIMEOUT);
      abortActiveRequest();
      window.location.assign(checkoutUrl);
    } catch (error) {
      checkoutStarted = false;
      setCheckoutButtonLoading(button, false);
      if (error && error.name !== 'AbortError') {
        console.error('[The Shift Method] Checkout gift sync error:', error);
        alert(
          'We could not add your free manifestation journal. Please refresh the cart and try again.'
        );
      }
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const checkoutButton = target.closest('#CartDrawer-Checkout, #checkout');
      if (checkoutButton) {
        startCheckout(event, checkoutButton);
        return;
      }

      const removeButton = target.closest('cart-remove-button');
      if (removeButton) {
        const row = cartRowFromTarget(removeButton);
        const mode = dependencyModeFromRow(row);

        if (mode) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          enforceDependencyRemoval({
            mode,
            clickedKey: row && row.dataset ? row.dataset.lineKey || '' : '',
            clickedIndex: Number(removeButton.dataset.index || (row && row.dataset.index) || 0),
            trigger: removeButton
          });
          return;
        }
      }

      const minusButton = target.closest('button[name="minus"]');
      const row = minusButton ? cartRowFromTarget(minusButton) : null;
      const mode = dependencyModeFromRow(row);
      if (!minusButton || !row || !mode) return;

      const quantityInput = row.querySelector('input.quantity__input');
      if (Number(quantityInput && quantityInput.value ? quantityInput.value : 0) > 1) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      enforceDependencyRemoval({
        mode,
        clickedKey: row.dataset.lineKey || '',
        clickedIndex: Number(
          (quantityInput && quantityInput.dataset.index) || row.dataset.index || 0
        ),
        trigger: minusButton
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const submitter = event.submitter;
      const isCheckoutForm =
        form.id === 'CartDrawer-Form' || form.id === 'cart' || form.action.includes('/cart');
      const isCheckoutSubmit =
        submitter && (submitter.id === 'CartDrawer-Checkout' || submitter.name === 'checkout');

      if (isCheckoutForm && isCheckoutSubmit) startCheckout(event, submitter);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const input = target ? target.closest('input.quantity__input') : null;
      const row = input ? cartRowFromTarget(input) : null;
      const mode = dependencyModeFromRow(row);
      if (!input || !row || !mode || Number(input.value) > 0) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      enforceDependencyRemoval({
        mode,
        clickedKey: row.dataset.lineKey || '',
        clickedIndex: Number(input.dataset.index || row.dataset.index || 0),
        trigger: input
      });
    },
    true
  );

  const initObserver = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    previousSnapshot = getDrawerSnapshot();
    const observer = new MutationObserver(scheduleReconcile);
    observer.observe(drawer, { childList: true, subtree: true });

    document.addEventListener('cartQuantityUpdated', scheduleReconcile);
    document.addEventListener('cartUpdate', scheduleReconcile);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initObserver, { once: true });
  } else {
    initObserver();
  }

  window.addEventListener('pageshow', () => {
    checkoutStarted = false;
    abortActiveRequest();
    previousSnapshot = getDrawerSnapshot();
  });
})();
